import { useEffect, useState } from '../vendor/react';

export type PosPaymentPreference = 'cash' | 'card' | 'transfer';
export type InterfaceDensity = 'comfortable' | 'compact';
export type SystemPreferences = {
  defaultPayment: PosPaymentPreference;
  vibration: boolean;
  confirmClearCart: boolean;
  autoOpenReceipt: boolean;
  density: InterfaceDensity;
  notifications: {
    lowStock: boolean;
    pendingOrders: boolean;
    financial: boolean;
    offline: boolean;
  };
};

const KEY = 'nexo.system.preferences.v1';
const EVENT = 'nexo:system-preferences';
export const defaultSystemPreferences: SystemPreferences = {
  defaultPayment: 'cash',
  vibration: true,
  confirmClearCart: true,
  autoOpenReceipt: true,
  density: 'comfortable',
  notifications: { lowStock: true, pendingOrders: true, financial: true, offline: true },
};

function readPreferences(): SystemPreferences {
  try {
    const stored = JSON.parse(window.localStorage.getItem(KEY) ?? '{}') as Partial<SystemPreferences>;
    return {
      ...defaultSystemPreferences,
      ...stored,
      notifications: { ...defaultSystemPreferences.notifications, ...(stored.notifications ?? {}) },
    };
  } catch {
    return defaultSystemPreferences;
  }
}

function persist(preferences: SystemPreferences) {
  try { window.localStorage.setItem(KEY, JSON.stringify(preferences)); } catch { /* Las preferencias siguen activas durante esta sesiÃ³n. */ }
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function useSystemPreferences() {
  const [preferences, setPreferences] = useState<SystemPreferences>(readPreferences);
  useEffect(() => {
    const sync = () => setPreferences(readPreferences());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(EVENT, sync); window.removeEventListener('storage', sync); };
  }, []);
  useEffect(() => { document.documentElement.dataset.density = preferences.density; }, [preferences.density]);

  const update = (patch: Partial<SystemPreferences>) => {
    const next = {
      ...preferences,
      ...patch,
      notifications: patch.notifications ? { ...preferences.notifications, ...patch.notifications } : preferences.notifications,
    };
    setPreferences(next);
    persist(next);
  };

  return {
    preferences,
    update,
    reset: () => { setPreferences(defaultSystemPreferences); persist(defaultSystemPreferences); },
  };
}
